import { AstroTransitionLink } from "@components/AstroTransitionLink"
import { AstroNewsViewTransitions } from "@components/AstroViewTransitions"
import NewsCard from "@components/News/NewsCard"
import "@components/News/NewsList/styles.css"
import { useRouteShellContext } from "@components/routeShellContext"
import { BigHeading, SiteShell } from "@components/SiteShell"
import { utcDateOnly } from "@dashboard-config"
import { newsListPageQueryOptions } from "@data/publicPageDataQuery"
import { newsTransitionName } from "@lib/viewTransitions"
import * as m from "@paraglide/messages"
import type { NewsItem } from "@site-types/content"
import type { NewsListPageData } from "@site-types/publicPageData"
import { createFileRoute } from "@tanstack/react-router"
import { localizedRouteHead } from "@utils/routeHead"
import { getLocalizedNews } from "@utils/siteData"

export const Route = createFileRoute("/$locale/news/")({
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(newsListPageQueryOptions(params.locale as "en" | "ja")),
  head: ({ params }) => {
    const locale = params.locale as "en" | "ja"
    return localizedRouteHead({ locale, page: "news", path: `/${locale}/news` })
  },
  component: RouteComponent,
})

function RouteComponent() {
  const data = Route.useLoaderData()
  return (
    <SiteShell>
      <NewsListPage data={data} />
    </SiteShell>
  )
}

const featuredNewsCount = 5

export function NewsListPage({ data }: { data: NewsListPageData }) {
  const { locale } = useRouteShellContext()
  const news = getLocalizedNews(data, locale)
  const featuredNews = news.slice(0, featuredNewsCount)
  const archiveNews = news.slice(featuredNewsCount)

  return (
    <>
      <AstroNewsViewTransitions news={news} />
      <BigHeading variant="h1">
        {(m.news_post_heading({}, { locale: locale }) || "News").toUpperCase()}
      </BigHeading>
      <div className="news-list-container container">
        <ul className="news-list news-list--featured">
          {featuredNews.map((post, index) => (
            <NewsListItem
              index={index}
              key={`${locale}-${post.slug?.current ?? "news"}-${index}`}
              post={post}
            />
          ))}
          {news.length === 0 ? <h5>No Records Found</h5> : null}
        </ul>
        {archiveNews.length > 0 ? (
          <ul
            aria-label="News archive"
            className="news-list news-list--archive news-list-archive"
          >
            {archiveNews.map((post, archiveIndex) => {
              const index = featuredNewsCount + archiveIndex
              return (
                <NewsListItem
                  className="news-list-archive__item"
                  index={index}
                  key={`${locale}-${post.slug?.current ?? "news"}-${index}`}
                  post={post}
                />
              )
            })}
          </ul>
        ) : null}
      </div>
    </>
  )
}

function NewsListItem({ className, index, post }: { className?: string; index: number; post: NewsItem }) {
  const { locale } = useRouteShellContext()
  const slug = post.slug?.current ?? ""
  const transitionName = newsTransitionName(slug)
  const displayDate = post.publishDate ? (utcDateOnly(post.publishDate) ?? "") : ""
  return (
    <li
      className={["news-list__item", `news-article__${index}`, className].filter(Boolean).join(" ")}
      data-id={`news-card-${slug || index}`}
      data-index={index}
      key={`${locale}-${slug || "news"}-${index}`}
    >
      <AstroTransitionLink
        to={`/${locale}/news/${slug}`}
        className="img-container"
        data-id={`news-card-image-link-${slug || index}`}
        transitionName={transitionName}
      >
        <NewsCard
          post={post}
          priority={index === 0}
        />
      </AstroTransitionLink>
      <h4>
        <AstroTransitionLink
          to={`/${locale}/news/${slug}`}
          data-id={`news-card-title-link-${slug || index}`}
          transitionName={transitionName}
        >
          {post.title}
        </AstroTransitionLink>
      </h4>
      <p className="blurb">{post.newsCoverBlurb}</p>
      <p className="date">{displayDate}</p>
    </li>
  )
}
